/**
 * Media queries as state.
 *
 * The viewer needs a *layout* decision in JavaScript, not only in CSS: below
 * a certain width it renders a different component tree -- one pane instead
 * of two, a horizontal rail instead of a vertical one -- rather than the
 * same tree restyled. A CSS breakpoint cannot do that, and rendering both
 * trees and hiding one would mount two copies of a 40-page filmstrip.
 *
 * `useSyncExternalStore` rather than `useState` + an effect: the first paint
 * then already has the right answer, so the viewer does not flash the
 * desktop layout on a phone before correcting itself.
 */
import { useCallback, useSyncExternalStore } from 'react'

/** Matches the `md` breakpoint the rest of the app uses. */
export const VIEWER_STACKS = '(max-width: 767.98px)'
/**
 * A portrait tablet. The document page is itself portrait, so a vertical
 * split would hand it half the width; a horizontal one hands it the full
 * width and half the height, and a tall page scrolls the way a page should.
 */
export const VIEWER_STACKS_Y = '(max-width: 1179.98px) and (orientation: portrait)'

export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      if (typeof matchMedia !== 'function') return () => {}
      const mq = matchMedia(query)
      mq.addEventListener('change', onChange)
      return () => mq.removeEventListener('change', onChange)
    },
    [query],
  )

  return useSyncExternalStore(
    subscribe,
    () => (typeof matchMedia === 'function' ? matchMedia(query).matches : false),
    // No server rendering here, but a build that ever adds it must not
    // guess: false means "the desktop layout", which degrades legibly.
    () => false,
  )
}

/**
 * A pointer that cannot hover precisely -- a finger or a stylus. This is the
 * same test the control-size tier in tokens.css uses, so the two never
 * disagree about what kind of device this is.
 */
export const usePointerCoarse = () => useMediaQuery('(pointer: coarse)')
